/**
 * Article Accordion Island — the shared, content-agnostic interaction shell for
 * the Magazine Layout system ("The Glass Box Accordion").
 *
 * Governed by cortex/drivers/product/magazine-layout-driver.md:
 *   A-1 — the interaction engine is content-agnostic. This island reads ONLY DOM
 *         structure + data-* attributes. There is NO hardcoded chapter count and
 *         NO content-shape assumption anywhere; it drives 1, 4, or 11 chapters
 *         identically. New content shapes are handled by adding components to the
 *         host markup, never by editing this file.
 *   A-5 — never cut content. A collapsed chapter's full body stays in the DOM
 *         (hidden via the `is-collapsed` class → `display:none` in CSS), never
 *         removed. The teaser previews the body; it never replaces it.
 *
 * Progressive enhancement (house style — see research-index-filter-island.ts):
 * with JS disabled or before hydration, no state classes exist, so every chapter
 * body is visible and every teaser is hidden (CSS defaults). The server-rendered
 * page is therefore already complete and readable; this island only enhances it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE MARKUP CONTRACT  (design targeted by the sibling composer + template work)
 * ─────────────────────────────────────────────────────────────────────────────
 * The composer / server template renders the following; this island reads it.
 *
 *   ROOT  (one per article; the island supports >1 root on a page)
 *     [data-article-accordion]
 *       — optional [data-accordion-initial="expanded"|"collapsed"]  (default: "collapsed")
 *       — optional [data-accordion-open-first="true"|"false"]        (default: "true")
 *       — optional child [data-accordion-toolbar]  — an explicit mount point for the
 *         spine + control bar. If absent, the island creates one and inserts it as
 *         the root's first child.
 *
 *   CHAPTER  (N per root, N ≥ 1 — the collapse/expand unit)
 *     [data-chapter]
 *       — optional [data-teaser="<verbatim teaser text>"]  — shown when collapsed.
 *         TEASER TEXT IS COMPOSER-SUPPLIED (per the driver's Teaser Extraction
 *         Rule); this island only RENDERS it, it never derives it.
 *       — required descendant [data-chapter-title]  — the always-visible click target.
 *       — required descendant [data-chapter-body]   — the full prose (e.g. wrapped in
 *         .article-body). Hidden, never removed, when collapsed.
 *     A chapter missing either required descendant is skipped (left untouched).
 *
 * ISLAND-MANAGED HOOKS  (added/removed at runtime — the composer must NOT hardcode these)
 *   • class `is-collapsed`  on [data-chapter]           — body hidden, teaser shown
 *   • class `is-current`    on [data-chapter]           — scrollspy: the current chapter
 *   • [data-accordion-toolbar]                          — spine + control bar wrapper
 *   • [data-accordion-spine]  →  N × [data-accordion-segment]  (word-count-weighted)
 *       - each segment carries inline `flex-grow` = its integer weight % (Σ = 100)
 *         and `data-weight-pct`; classes `is-filled` (opened or scrolled-past) and
 *         `is-current` (scrollspy) are toggled on it — the current-marking is a
 *         CLASS toggle, never an inline style.
 *   • [data-accordion-teaser]  — the teaser <p> injected from [data-teaser]
 *   • [data-accordion-toggle-all]  — the Expand-all / Collapse-all control
 *   • [data-accordion-ready]  on the root — set once hydration completes
 *   • a11y wiring: title → role/tabindex (when not already a native control),
 *     aria-expanded + aria-controls; body → role="region" + aria-labelledby.
 */

export interface ArticleAccordionIslandProps {
  /** Initial state for every chapter. Default "collapsed" (magazine-scannable). */
  readonly initial?: 'collapsed' | 'expanded';
  /** Auto-expand the first chapter on hydrate (ignored when initial="expanded"). Default true. */
  readonly openFirst?: boolean;
}

/** One resolved chapter: its box, click target, prose body, and spine segment. */
interface ChapterRecord {
  readonly el: HTMLElement;
  readonly title: HTMLElement;
  readonly body: HTMLElement;
  readonly segment: HTMLElement;
}

/** Module-scoped counter guaranteeing unique ids across multiple accordions on a page. */
let accordionInstanceCount = 0;

/**
 * Server render: a hidden host marker. The chapter markup itself is composer-
 * rendered per the contract above; the marker only anchors hydration (mirrors
 * research-index-filter-island.ts).
 */
export function render(props: Readonly<Record<string, unknown>>): string {
  const initial = props.initial === 'expanded' ? 'expanded' : 'collapsed';
  const openFirst = props.openFirst === false ? 'false' : 'true';
  return `<div data-article-accordion-host data-initial="${initial}" data-open-first="${openFirst}" hidden></div>`;
}

/** First descendant matching `selector` whose OWNING chapter/root is `scope` (ignores nested roots). */
function ownDescendant(
  scope: HTMLElement,
  selector: string,
  boundarySelector: string
): HTMLElement | null {
  const matches = scope.querySelectorAll<HTMLElement>(selector);
  for (const el of matches) {
    if (el.closest(boundarySelector) === scope) {
      return el;
    }
  }
  return null;
}

/**
 * Integer percentages proportional to `weights`, guaranteed to sum to exactly 100
 * (largest-remainder method). Zero/empty totals fall back to an equal split — so a
 * weight of 0 never hides a chapter's segment entirely.
 */
function computePercentages(weights: readonly number[]): number[] {
  const n = weights.length;
  if (n === 0) {
    return [];
  }

  const total = weights.reduce((sum, w) => sum + Math.max(0, w), 0);
  if (total <= 0) {
    const base = Math.floor(100 / n);
    const out = new Array<number>(n).fill(base);
    let remainder = 100 - base * n;
    for (let i = 0; i < n && remainder > 0; i += 1, remainder -= 1) {
      out[i] = (out[i] ?? base) + 1;
    }
    return out;
  }

  const exact = weights.map((w) => (Math.max(0, w) / total) * 100);
  const out = exact.map((x) => Math.floor(x));
  let remainder = 100 - out.reduce((sum, x) => sum + x, 0);

  // Hand the leftover points to the largest fractional parts.
  const byFraction = exact
    .map((x, i) => ({ i, frac: x - Math.floor(x) }))
    .sort((a, b) => b.frac - a.frac);

  for (let k = 0; k < byFraction.length && remainder > 0; k += 1, remainder -= 1) {
    const idx = byFraction[k]?.i;
    if (idx === undefined) {
      break;
    }
    out[idx] = (out[idx] ?? 0) + 1;
  }
  return out;
}

/** Wire one accordion root. Content-agnostic: everything derives from the chapters found. */
function setupAccordion(root: HTMLElement, config: Required<ArticleAccordionIslandProps>): void {
  // Chapters belonging to THIS root only (a nested accordion keeps its own chapters).
  const chapterEls = Array.from(root.querySelectorAll<HTMLElement>('[data-chapter]')).filter(
    (el) => el.closest('[data-article-accordion]') === root
  );

  // Resolve each chapter's required parts; skip malformed chapters.
  const resolved = chapterEls
    .map((el) => ({
      el,
      title: ownDescendant(el, '[data-chapter-title]', '[data-chapter]'),
      body: ownDescendant(el, '[data-chapter-body]', '[data-chapter]'),
    }))
    .filter((c): c is { el: HTMLElement; title: HTMLElement; body: HTMLElement } =>
      c.title !== null && c.body !== null
    );

  if (resolved.length === 0) {
    return; // Nothing to enhance — leave the server-rendered content exactly as-is.
  }

  const idBase = root.id || `article-accordion-${(accordionInstanceCount += 1)}`;

  // ── Spine (word-count-weighted) ────────────────────────────────────────────
  const spine = document.createElement('div');
  spine.setAttribute('data-accordion-spine', '');
  spine.setAttribute('role', 'group');
  spine.setAttribute('aria-label', 'Chapter progress');

  const percentages = computePercentages(resolved.map((c) => c.body.textContent?.length ?? 0));

  const chapters: ChapterRecord[] = resolved.map((c, index) => {
    const segment = document.createElement('button');
    segment.type = 'button';
    segment.setAttribute('data-accordion-segment', '');
    const pct = percentages[index] ?? 0;
    segment.style.flexGrow = String(pct);
    segment.dataset.weightPct = String(pct);
    const label = c.title.textContent?.trim() || `Chapter ${index + 1}`;
    segment.setAttribute('aria-label', label);
    segment.title = label;
    spine.appendChild(segment);
    return { el: c.el, title: c.title, body: c.body, segment };
  });

  // ── Toggle-all control ─────────────────────────────────────────────────────
  const toggleAll = document.createElement('button');
  toggleAll.type = 'button';
  toggleAll.setAttribute('data-accordion-toggle-all', '');

  // ── Toolbar (reuse an explicit mount, else create + prepend) ────────────────
  const existingToolbar = ownDescendant(root, '[data-accordion-toolbar]', '[data-article-accordion]');
  const toolbar = existingToolbar ?? document.createElement('div');
  if (!existingToolbar) {
    toolbar.setAttribute('data-accordion-toolbar', '');
  }
  toolbar.appendChild(spine);
  toolbar.appendChild(toggleAll);
  if (!existingToolbar) {
    root.insertBefore(toolbar, root.firstChild);
  }

  // ── State helpers ───────────────────────────────────────────────────────────
  const isCollapsed = (rec: ChapterRecord): boolean => rec.el.classList.contains('is-collapsed');

  function setCollapsed(rec: ChapterRecord, collapsed: boolean): void {
    rec.el.classList.toggle('is-collapsed', collapsed);
    rec.title.setAttribute('aria-expanded', String(!collapsed));
  }

  function currentIndex(): number {
    // The current chapter is the last one whose top edge has crossed a line at
    // 30% of the viewport height — a stable read for both tall and short chapters.
    const line = window.innerHeight * 0.3;
    let current = 0;
    chapters.forEach((rec, index) => {
      if (rec.el.getBoundingClientRect().top - line <= 0) {
        current = index;
      }
    });
    return current;
  }

  function refreshToggleAll(): void {
    const allOpen = chapters.every((rec) => !isCollapsed(rec));
    toggleAll.textContent = allOpen ? 'Collapse all' : 'Expand all';
    toggleAll.setAttribute('aria-label', allOpen ? 'Collapse all chapters' : 'Expand all chapters');
  }

  function update(): void {
    const current = currentIndex();
    chapters.forEach((rec, index) => {
      const open = !isCollapsed(rec);
      const marked = index === current;
      rec.el.classList.toggle('is-current', marked);
      rec.segment.classList.toggle('is-current', marked);
      rec.segment.classList.toggle('is-filled', open || index <= current);
    });
    refreshToggleAll();
  }

  function toggle(rec: ChapterRecord): void {
    setCollapsed(rec, !isCollapsed(rec));
    update();
  }

  // ── Per-chapter wiring (title control, teaser, a11y) ────────────────────────
  chapters.forEach((rec, index) => {
    const tag = rec.title.tagName;
    const nativeControl = tag === 'BUTTON' || tag === 'A';
    if (!nativeControl) {
      rec.title.setAttribute('role', 'button');
      rec.title.setAttribute('tabindex', '0');
    }
    if (!rec.title.id) {
      rec.title.id = `${idBase}-title-${index}`;
    }
    if (!rec.body.id) {
      rec.body.id = `${idBase}-body-${index}`;
    }
    rec.body.setAttribute('role', 'region');
    rec.body.setAttribute('aria-labelledby', rec.title.id);
    rec.title.setAttribute('aria-controls', rec.body.id);

    rec.title.addEventListener('click', () => toggle(rec));
    rec.title.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggle(rec);
      }
    });

    // Teaser: render the composer-supplied verbatim text; never derive it here.
    const teaserText = rec.el.getAttribute('data-teaser');
    if (teaserText && !ownDescendant(rec.el, '[data-accordion-teaser]', '[data-chapter]')) {
      const teaser = document.createElement('p');
      teaser.setAttribute('data-accordion-teaser', '');
      teaser.textContent = teaserText;
      rec.title.insertAdjacentElement('afterend', teaser);
    }

    // A segment click jumps to its chapter and opens it.
    rec.segment.addEventListener('click', () => {
      setCollapsed(rec, false);
      rec.el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      update();
    });
  });

  // ── Initial state ───────────────────────────────────────────────────────────
  chapters.forEach((rec, index) => {
    const collapse = config.initial === 'expanded' ? false : !(config.openFirst && index === 0);
    setCollapsed(rec, collapse);
  });

  // ── Toggle-all + scroll/resize listeners ────────────────────────────────────
  toggleAll.addEventListener('click', () => {
    const collapseNext = chapters.every((rec) => !isCollapsed(rec));
    chapters.forEach((rec) => setCollapsed(rec, collapseNext));
    update();
  });

  let ticking = false;
  const onScrollOrResize = (): void => {
    if (ticking) {
      return;
    }
    ticking = true;
    window.requestAnimationFrame(() => {
      update();
      ticking = false;
    });
  };
  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize, { passive: true });

  // ── Anchor reveal ────────────────────────────────────────────────────────────
  // An in-page link whose target lives inside a COLLAPSED chapter (footnote refs
  // `[n](#fnN)`, or any #fragment) can't be scrolled to — the target is display:none
  // (A-5 collapse). Open the containing chapter first (never collapse an open one),
  // then scroll. Fires on click within this root, on hashchange, and for a hash
  // present at load (a shared deep link like …#fn6).
  const revealTarget = (rawHash: string): void => {
    if (!rawHash || rawHash.length < 2) return;
    let id: string;
    try {
      id = decodeURIComponent(rawHash.slice(1));
    } catch {
      id = rawHash.slice(1);
    }
    if (!id) return;
    let target: HTMLElement | null = null;
    try {
      target = root.querySelector<HTMLElement>(`#${CSS.escape(id)}`);
    } catch {
      target = null;
    }
    if (!target) return; // not inside this accordion — leave default behavior alone
    const found: HTMLElement = target;
    const rec = chapters.find((r) => r.body.contains(found) || r.el.contains(found));
    if (rec && isCollapsed(rec)) {
      setCollapsed(rec, false); // open if collapsed; never collapse an already-open one
      update();
      // The chapter just changed layout, and the browser's own hash scroll already
      // fired against a display:none target and did nothing — so scroll on the next
      // frame, once the now-visible target has a box. Scoped to the just-opened case:
      // when the chapter was already open the native hash scroll suffices, and a
      // second RAF scroll would double-animate to the same spot.
      window.requestAnimationFrame(() => {
        found.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  root.addEventListener('click', (event: MouseEvent) => {
    const link = (event.target as HTMLElement | null)?.closest<HTMLAnchorElement>('a[href]');
    const href = link?.getAttribute('href') ?? '';
    if (!href.startsWith('#')) return;
    // Don't preventDefault: let the URL hash update (shareable + back-button); we
    // only guarantee the target's chapter is open, then scroll it into view.
    revealTarget(href);
  });
  window.addEventListener('hashchange', () => revealTarget(window.location.hash));
  if (window.location.hash) {
    window.requestAnimationFrame(() => revealTarget(window.location.hash));
  }

  root.setAttribute('data-accordion-ready', '');
  update();
}

/** Client hydrate: enhance every accordion root on the page. The marker `_el` is unused. */
export default {
  hydrate(_el: HTMLElement, props: ArticleAccordionIslandProps): void {
    const config: Required<ArticleAccordionIslandProps> = {
      initial: props.initial === 'expanded' ? 'expanded' : 'collapsed',
      openFirst: props.openFirst ?? true,
    };
    const roots = document.querySelectorAll<HTMLElement>('[data-article-accordion]');
    roots.forEach((root) => setupAccordion(root, config));
  },
};

// ── Self-bootstrap for the explicit <script src> integration path ─────────────
// The research-article page loads this island via a plain
// `<script src="/islands/article-accordion-island.js" defer>` tag and does NOT
// emit the generic [data-island] hydrator (composeResearchArticlePage collects no
// server islands), so — like cite-modal-island and every other explicitly-scripted
// island (the house pattern) — the module must hydrate itself on load. Per-page
// config is read from the first root's documented data-* attributes; the
// data-accordion-ready guard makes this a no-op for any root the generic hydrator
// already wired, so the two paths never double-initialize.
if (typeof document !== 'undefined') {
  const boot = (): void => {
    const first = document.querySelector<HTMLElement>('[data-article-accordion]');
    const config: Required<ArticleAccordionIslandProps> = {
      initial: first?.getAttribute('data-accordion-initial') === 'expanded' ? 'expanded' : 'collapsed',
      openFirst: first?.getAttribute('data-accordion-open-first') !== 'false',
    };
    document.querySelectorAll<HTMLElement>('[data-article-accordion]').forEach((root) => {
      if (root.hasAttribute('data-accordion-ready')) return;
      setupAccordion(root, config);
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}
